import React, { createContext, useContext, useEffect, useState } from 'react';
import Loading from '../components/Loading';
import { getCookie } from '../utilities/cookies';
import { env, handleAxiosError } from '../utilities/function';

import useSnack from '../hooks/useSnack';
import { setCookie } from "../utilities/cookies";
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const authorizeContext = createContext();

const authServer = axios.create({
    baseURL: env('AUTHENTICATION_SERVER'),
});

const accessToken = getCookie('accessToken');
authServer.defaults.withCredentials = false;
authServer.defaults.headers.Authorization = `Bearer ${accessToken}`;

const AuthorizationProvider = ({ children }) => {
    const [content, setContent] = useState(<Loading message='Please wait, logging you in...' />);
    const [user, setUser] = useState({});
    const { showMessage } = useSnack();
    const navigate = useNavigate();

    const authorize = async (loggedIn, cb) => {
        if (loggedIn) { 
          setContent(children);
        } else {
            const redirectTo = "/signup"
            setContent(
                <Loading
                    message='Please wait, redirecting you to Login page...'
                    redirectTo={redirectTo}
                />
            );
        }
        if (typeof cb === 'function') cb(setUser);
    };

    const createSession = async (refreshToken, user) => {
        // If your backend needs to open a session, do it here
        // For now, we follow the pattern of just authorizing
        authorize(true, (setUser) => setUser(user));
    };

    useEffect(() => {
        (async () => {
            try {
                const queryParameters = new URLSearchParams(window.location.search);
                const urlUserId = queryParameters.get("userId");
                const urlRefreshToken = queryParameters.get("refreshToken");

                if (urlUserId) {
                    // SSO Redirect flow
                    console.log("SSO login detected, fetching profile...");
                    
                    const formData = new FormData();
                    formData.append("id", urlUserId);
                    const response = await fetch(
                      `${process.env.REACT_APP_ACCOUNTS_API_URL}/auth/get_user_profile`,
                      {
                        method: "POST",
                        body: formData,
                      }
                    );
          
                    if (response.ok) {
                      const responseData = await response.json();
                      const { user } = responseData;
                      
                      if (user) {
                        // Clear old data
                        localStorage.clear();
                        
                        // Set new data
                        const refreshToken = urlRefreshToken || user.refreshToken;
                        user.refreshToken = refreshToken;
                        
                        localStorage.setItem("user", JSON.stringify(user));
                        if (user.accessToken || user.token) {
                            localStorage.setItem("token", user.accessToken || user.token);
                            setCookie("accessToken", user.accessToken || user.token);
                        }
                        
                        setCookie("userId", user._id);
                        setCookie("refreshToken", refreshToken);
                        setCookie("fullName", user.username || "");
                        setCookie("role", user.role || "user");

                        await createSession(refreshToken, user);
                        
                        // Navigate to ListOrganization and remove query params from URL
                        navigate("/ListOrganization", { replace: true });
                      } else {
                        console.error("User object missing in profile response");
                        authorize(false);
                      }
                    } else {
                      console.error("Failed to fetch user profile from SSO");
                      authorize(false);
                    }
                } else if (localStorage.getItem("user")) {
                    // Regular session restoration
                    const user = JSON.parse(localStorage.getItem("user"));
                    await createSession(user.refreshToken, user);
                } else {
                    // No user found
                    authorize(false);
                }
            } catch (err) {
                console.error("Authorization error:", err);
                // Use a safe error message if SnackBar is not ready
                if (showMessage) {
                    showMessage({ error: "Authorization failed. Please login again." });
                }
                authorize(false);
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <authorizeContext.Provider value={{ authorize, setUser, user, setContent }}>
            {content}
        </authorizeContext.Provider>
    );
};

const useAuthorize = () => useContext(authorizeContext).authorize;
const useUser = () => useContext(authorizeContext)?.user;
const useSetUser = () => useContext(authorizeContext).setUser;
const useSetContent = () => useContext(authorizeContext).setContent;

export default AuthorizationProvider;
export { useAuthorize, useUser, useSetUser, useSetContent };